/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1781049600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jun 10 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-sheets",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Google Sheets",
          "depth": 0,
          "id": "connect-to-google-sheets"
        }, {
          "value": "Authenticate with a user account",
          "depth": 1,
          "id": "authenticate-with-a-user-account"
        }, {
          "value": "Authenticate with a service account",
          "depth": 1,
          "id": "authenticate-with-a-service-account"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Sheet selection",
          "depth": 1,
          "id": "sheet-selection"
        }, {
          "value": "Data format",
          "depth": 1,
          "id": "data-format"
        }, {
          "value": "Enable new sheet creation",
          "depth": 1,
          "id": "enable-new-sheet-creation"
        }, {
          "value": "Data range selection",
          "depth": 1,
          "id": "data-range-selection"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Rate limits",
          "depth": 1,
          "id": "rate-limits"
        }, {
          "value": "Find sheets originating from an Excel file upload",
          "depth": 1,
          "id": "find-sheets-originating-from-an-excel-file-upload"
        }, {
          "value": "Resolve \"This app is blocked\" errors",
          "depth": 1,
          "id": "resolve-this-app-is-blocked-errors"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      code: "code",
      ol: "ol",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {RowAndColumnOrderingInfo, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!RowAndColumnOrderingInfo) _missingMdxReference("RowAndColumnOrderingInfo", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#sync-configuration",
                children: _jsx(_components.strong, {
                  children: "Sheets"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to a Google Sheets spreadsheet"
            }), _jsx(_components.td, {
              children: "Mirror, Snapshot, Append"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.google.com/sheets/api/reference/rest/v4/spreadsheets.values/update",
                children: "Update spreadsheet values ↗"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(RowAndColumnOrderingInfo, {
        variant: "spreadsheet"
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-google-sheets",
        children: "Connect to Google Sheets"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button.\nHightouch provides two Google Sheets options:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Google Sheets - User Account"
          }), ": Use this option if you want to authenticate access to Hightouch with a particular user's login. This gives Hightouch access to any Google Sheet the user has Editor access to. This option uses OAuth for authentication."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Google Sheets - Service account (Recommended)"
          }), ": This option provides fine-grained control over access to your data."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-connect.png",
          alt: "Connection selection"
        })
      }), "\n", _jsx(_components.p, {
        children: "Using a service account is best practice for a few reasons:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "You can grant service accounts access to specific sheets. This means that only the necessary permissions are granted to the service account, instead of allowing access to all files belonging to a user. This reduces the risk of unauthorized access to sensitive data or functionality."
        }), "\n", _jsx(_components.li, {
          children: "Service accounts are designed to represent applications or services, not individuals. You don't have to worry about disruptions to your syncs if the user who authenticated has their permissions changed or if they leave the organization."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-a-user-account",
        children: "Authenticate with a user account"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "Google Sheets - User Account"
        }), " destination option and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ".\nThen select to ", _jsx(_components.strong, {
          children: "Log in to Google Sheets"
        }), ", select your Google account, and click ", _jsx(_components.strong, {
          children: "Allow"
        }), " to the requested permissions."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-oauth.png",
          alt: "OAuth example"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch uses the same OAuth mechanism for all Google destinations. If you've\nset up other Google destinations, such as Google Ads, Campaign Manager, or\nDisplay & Video 360, they appear here."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["After authorizing, Hightouch connects to Google Sheets.\nClick ", _jsx(_components.strong, {
          children: "Continue"
        }), " and complete setup by giving the destination a descriptive name."]
      }), "\n", _jsx(_components.h3, {
        id: "authenticate-with-a-service-account",
        children: "Authenticate with a service account"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "Google Sheets (Service Account)"
        }), " destination option and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ".\nThen, click ", _jsx(_components.strong, {
          children: "Generate a service account"
        }), ".\nHightouch then generates a ", _jsx(_components.strong, {
          children: "Google Service Account Email"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-finalize.png",
          alt: "Service Account settings"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "Continue"
        }), " and complete setup by giving the destination a descriptive name."]
      }), "\n", _jsxs(_components.p, {
        children: ["To give Hightouch access to a particular spreadsheet, share the spreadsheet with the service account email with ", _jsx(_components.strong, {
          children: "Editor"
        }), " permissions."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-share.png",
          alt: "Share spreadsheet"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can always access the service account email from the destination's overview page, should you need to share additional sheets."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-share-1.png",
          alt: "Share spreadsheet"
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Google Sheets destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Google Sheets destination you want to sync to."]
      }), "\n", _jsxs(_components.p, {
        children: ["The Google Sheets destination allows you to ", _jsx(_components.strong, {
          children: "Mirror"
        }), " your data to a spreadsheet or create a new sheet on every sync with ", _jsx(_components.strong, {
          children: "Snapshot"
        }), " mode.\nYou can also add data to an existing sheet with ", _jsx(_components.strong, {
          children: "Append"
        }), " mode. In this mode, rows added to your model are added to the sheet. Rows changed and removed in your model are ignored. This may result in faster syncs."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Mirror"
          }), " mode replaces the entire selected sheet's contents with the rows\nfrom your model. Ensure that your sheet doesn't contain data that shouldn't be\noverwritten if you select this mode."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "sheet-selection",
        children: "Sheet selection"
      }), "\n", _jsxs(_components.p, {
        children: ["You can choose to select the Google Sheets file you want to update either by its ", _jsx(_components.strong, {
          children: "name"
        }), " using the sheets picker (recommended) or inputting the Google Sheet's ", _jsx(_components.strong, {
          children: "ID"
        }), ".\nYou can find your spreadsheet's ID in its URL: ", _jsx(_components.code, {
          children: "https://docs.google.com/spreadsheets/d/<spreadsheetId>/edit#gid=0"
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-select-by-id.png",
          alt: "Select Google Sheet by ID"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't see your spreadsheet's name in the dropdown, make sure you authorized the right account or invited the Hightouch service account email to your spreadsheet,\ndepending on your ", _jsx(_components.a, {
          href: "#connect-to-google-sheets",
          children: "setup mode"
        }), ".\nIf you updated a sheet's permissions, click the refresh button to see newly authorized sheets."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-refresh.png",
          alt: "Select Google Sheet by ID"
        })
      }), "\n", _jsx(_components.p, {
        children: "Then, select the sheet within the spreadsheet that you would like to sync to."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-config.png",
          alt: "Sheet settings in sync configuration"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Colons (", _jsx(_components.code, {
            children: ":"
          }), ") in spreadsheet and sheet titles can cause errors. Ensure your\nspreadsheet and sheet titles don't include any."]
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Avoid setting up multiple syncs to the same sheet, as this can corrupt your\ndata."
        })
      }), "\n", _jsx(_components.h3, {
        id: "data-format",
        children: "Data format"
      }), "\n", _jsx(_components.p, {
        children: "Google Sheets accepts inputted data in two ways:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Raw"
          }), " Won't parse the values the user has entered and stores values as-is. Keep in mind that Hightouch does some data casting and may send numbers and dates as strings. This process can add a leading apostrophe to values in Google Sheets cells."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User entered"
          }), " Parses values as if the user typed them into the UI. Numbers stay as numbers, but strings may convert to numbers, dates, etc. following the same rules that apply when entering text into a cell via the Google Sheets UI."]
        }), "\n"]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you want to sync numbers, dates, or other non-string values in their\noriginal data type and not converted to strings, use ", _jsx(_components.strong, {
            children: "User entered"
          }), " mode."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-data.png",
          alt: "Data input settings in sync configuration"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["If you are inserting null values using the ", _jsx(_components.strong, {
            children: "Raw"
          }), " data format, these won't\nregister as blank fields though they may appear to be. Keep this in mind if\nyou are using formulas that are looking for null values. If any of your\nformulas rely on blank fields, it's best to use ", _jsx(_components.strong, {
            children: "User entered"
          }), " as the input\nmode."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For more information, read the ", _jsx(_components.a, {
          href: "https://developers.google.com/sheets/api/reference/rest/v4/ValueInputOption",
          children: "Google documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "enable-new-sheet-creation",
        children: "Enable new sheet creation"
      }), "\n", _jsx(_components.p, {
        children: "Google Sheets has a limit of 10 million cells in a sheet.\nIf your sync contains more data than this, you can enable Hightouch to create new sheets with the additional data.\nFor each additional 10 million cells, Hightouch creates a new sheet with the original sheet's name suffixed with an underscore and a number.\nFor example, \"sheetname_1\" for the first additional 10 million cells, \"sheetname_2\" for the second 10 million cells, etc.\nEnsure these names don't conflict with existing sheets' names to avoid them being overwritten."
      }), "\n", _jsxs(_components.p, {
        children: ["To use this configuration option, ensure the ", _jsx(_components.a, {
          href: "#connect-to-google-sheets",
          children: "user you authenticated with"
        }), " has access to the spreadsheet's folder, not just the spreadsheet itself."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch released enabling new sheet creation on June 22, 2023. If you set up\nyour Google Sheets destination before this date and want to use this option,\nyou need to ", _jsx(_components.a, {
            href: "#connect-to-google-sheets",
            children: "reauthorize your connection"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "data-range-selection",
        children: "Data range selection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports selecting a manual range in your Google Sheets to write data to.\nThis allows you to preserve formulas, notes, or other data in your spreadsheets' margins."
      }), "\n", _jsxs(_components.p, {
        children: ["To use a manual range enter a range into the ", _jsx(_components.strong, {
          children: "Custom Cell Range"
        }), " input in the format ", _jsx(_components.code, {
          children: "A1:Z1000"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-range.png",
          alt: "Range setting in sync configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: "If you leave this field blank, Hightouch mirrors data to the entire spreadsheet, dependent on your model's query results."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Google Sheets, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#google-sheets",
            children: "Error codes:\nGoogle Sheets"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "rate-limits",
        children: "Rate limits"
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive an error from Google if you exceed your quota for write requests during a sync: ", _jsx(_components.code, {
          children: "Quota exceeded for quota metric"
        }), ".\nFor more information, read the Google ", _jsx(_components.a, {
          href: "https://developers.google.com/sheets/api/limits",
          children: "usage limit documentation"
        }), ".\nTo avoid this error:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["stagger or schedule your syncs to Google Sheets to ", _jsx(_components.a, {
            href: "/syncs/schedule-sync-with-sequences",
            children: "run in sequence"
          }), " to avoid simultaneous write requests between syncs"]
        }), "\n", _jsx(_components.li, {
          children: "set up multiple Google Sheets destinations in your workspace so as to reduce the number of syncs that use the same destination"
        }), "\n", _jsxs(_components.li, {
          children: ["set up ", _jsx(_components.a, {
            href: "#authenticate-with-a-service-account",
            children: "Google Sheets (Service Account)"
          }), " destinations rather than ", _jsx(_components.a, {
            href: "#authenticate-with-a-user-account",
            children: "authenticating with a user account"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["reach out to Google to request a ", _jsx(_components.a, {
            href: "https://developers.google.com/sheets/api/limits#increase",
            children: "quota increase"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "find-sheets-originating-from-an-excel-file-upload",
        children: "Find sheets originating from an Excel file upload"
      }), "\n", _jsxs(_components.p, {
        children: ["You may have trouble selecting that Google Sheet that was originally uploaded to Google as an Excel file.\nIf you are the owner of the file, but can't find the spreadsheet in the ", _jsx(_components.a, {
          href: "#sheet-selection",
          children: "sheet selection dropdown"
        }), ", confirm that it's not an ", _jsx(_components.code, {
          children: ".XLS"
        }), " or ", _jsx(_components.code, {
          children: ".XLSX"
        }), " file.\nYou can see the file type of a file next to its name in the Google Sheets UI."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-xls.png",
          alt: "File type example"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch can only update Google Sheets files, not ", _jsx(_components.code, {
          children: ".XLS"
        }), " or ", _jsx(_components.code, {
          children: ".XLSX"
        }), " files. To convert a file to a Google Sheets file, go to ", _jsx(_components.strong, {
          children: "File → Save as Google Sheets"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-convert.png",
          alt: "Convert to Google Sheet dropdown"
        })
      }), "\n", _jsx(_components.p, {
        children: "After converting the file to a Google sheet, it becomes available in the dropdown selector."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "You may need to click the refresh button next to the dropdown selector for\nspreadsheets after you've converted a sheet, or updated its access."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to work with Excel files, consider using the ", _jsx(_components.a, {
          href: "/destinations/microsoft-excel",
          children: "Microsoft Excel destination"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "resolve-this-app-is-blocked-errors",
        children: "Resolve \"This app is blocked\" errors"
      }), "\n", _jsx(_components.p, {
        children: "If you see a \"This app is blocked\" error when trying to authorize Hightouch with your Google account, your Google Workspace admin may need to re-approve Hightouch's access. This can happen after Google API verification policy changes."
      }), "\n", _jsx(_components.p, {
        children: "To resolve the error:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to your ", _jsx(_components.a, {
            href: "https://myaccount.google.com/",
            children: "Google Account"
          }), " → ", _jsx(_components.strong, {
            children: "Data & Privacy"
          }), " → ", _jsx(_components.strong, {
            children: "Third-party apps & services"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Find ", _jsx(_components.strong, {
            children: "Hightouch"
          }), " in the list and select ", _jsx(_components.strong, {
            children: "See details"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Remove access"
          }), " to revoke the existing authorization."]
        }), "\n", _jsx(_components.li, {
          children: "Return to Hightouch and re-authorize the Google Sheets destination."
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Removing access disconnects all existing Google integrations that use this\naccount. Any syncs using these integrations stop running until you\nre-authorize. Before removing access, confirm which syncs rely on this Google\naccount."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This issue can affect any Google destination that uses OAuth, including ", _jsx(_components.a, {
          href: "/destinations/google-drive",
          children: "Google Drive"
        }), ", ", _jsx(_components.a, {
          href: "/destinations/google",
          children: "Google Ads"
        }), ", and ", _jsx(_components.a, {
          href: "/destinations/google-analytics",
          children: "Google Analytics"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After your first sync completes, open the target spreadsheet in Google Sheets and confirm:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The expected columns appear in the correct order."
        }), "\n", _jsx(_components.li, {
          children: "Row counts roughly match the number of records in your model."
        }), "\n", _jsxs(_components.li, {
          children: ["If using ", _jsx(_components.strong, {
            children: "Mirror"
          }), " mode, verify that previous sheet contents were replaced as expected."]
        }), "\n", _jsxs(_components.li, {
          children: ["If using ", _jsx(_components.strong, {
            children: "Snapshot"
          }), " mode, confirm a new sheet was created with the correct data."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error message"
            }), _jsx(_components.th, {
              align: "left",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Action would increase the number of cells in the workbook above the limit of 10000000 cells"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Spreadsheet exceeds the 10 million cell limit. ", _jsx(_components.a, {
                href: "#action-would-increase-the-number-of-cells-in-the-workbook-above-the-limit-of-10000000-cells",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Cannot read properties of null (reading 'properties')"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The selected sheet was renamed or deleted. ", _jsx(_components.a, {
                href: "#cannot-read-properties-of-null-reading-properties",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Internal error encountered"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Charts or pivot tables in the spreadsheet cause timeouts. ", _jsx(_components.a, {
                href: "#internal-error-encountered",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Invalid values: list_value"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Object or array columns are not supported. ", _jsx(_components.a, {
                href: "#invalid-values-list_value",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Range {...} exceeds grid limits"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The sheet or range is protected. ", _jsx(_components.a, {
                href: "#range--exceeds-grid-limits",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Requested writing within range...but tried writing to column"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Sheet name contains a colon. ", _jsx(_components.a, {
                href: "#requested-writing-within-rangebut-tried-writing-to-column",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "The caller does not have permission"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The sheet is not shared with the authenticated account. ", _jsx(_components.a, {
                href: "#the-caller-does-not-have-permission",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Unable to parse range"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The selected sheet was renamed or deleted. ", _jsx(_components.a, {
                href: "#unable-to-parse-range",
                children: "More detail."
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: "Permission errors using a sheet ID"
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Sheet must be selected in the picker before using its ID. ", _jsx(_components.a, {
                href: "#permission-errors-using-a-sheet-id",
                children: "More detail."
              })]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "action-would-increase-the-number-of-cells-in-the-workbook-above-the-limit-of-10000000-cells",
        children: "Action would increase the number of cells in the workbook above the limit of 10,000,000 cells"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["Action would increase the number of cells ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "in"
          }), " the workbook above the ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "limit"
          }), " of 10000000 cells\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Your model's data exceeds the Google Sheets 10 million cell limit for a single spreadsheet."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Enable Hightouch to ", _jsx(_components.a, {
          href: "#enable-new-sheet-creation",
          children: "create new sheets"
        }), ". When enabled, Hightouch automatically creates additional sheets if your data surpasses the limit."]
      }), "\n", _jsx(_components.h4, {
        id: "cannot-read-properties-of-null-reading-properties",
        children: "Cannot read properties of null (reading 'properties')"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-arduino",
          children: _jsxs(_components.span, {
            className: "hljs-function",
            children: ["Cannot read properties of ", _jsx(_components.span, {
              className: "hljs-title",
              children: "null"
            }), " ", _jsxs(_components.span, {
              className: "hljs-params",
              children: ["(reading ", _jsx(_components.span, {
                className: "hljs-string",
                children: "'properties'"
              }), ")"]
            }), "\n"]
          })
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sheet within the spreadsheet selected in the sync configuration was renamed or deleted in Google Sheets."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " ", _jsx(_components.a, {
          href: "#sheet-selection",
          children: "Select a new sheet"
        }), " from the dropdown menu in your sync configuration."]
      }), "\n", _jsx(_components.h4, {
        id: "internal-error-encountered",
        children: "Internal error encountered"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-go",
          children: ["Internal ", _jsx(_components.span, {
            className: "hljs-type",
            children: "error"
          }), " encountered\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The spreadsheet contains sheets with charts or pivot tables, which can cause Google API timeouts."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Move the data you want Hightouch to update to a separate spreadsheet. Reference this data from your original spreadsheet using a formula like ", _jsx(_components.a, {
          href: "https://support.google.com/docs/answer/3093340?hl=en",
          children: _jsx(_components.code, {
            children: "IMPORTRANGE"
          })
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "invalid-values-list_value",
        children: "Invalid values: list_value"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Invalid values ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[1]"
          }), _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[2]"
          }), ": list_value\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The Google Sheets destination doesn't support syncing object or array type columns."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Change the type of any object or array columns to ", _jsx(_components.strong, {
          children: "String"
        }), " ", _jsx(_components.a, {
          href: "/models/data-types-casting#casting-from-your-model-configuration",
          children: "in your model configuration"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-google-sheets-column-type.png",
          alt: "Change column type"
        })
      }), "\n", _jsx(_components.h4, {
        id: "range--exceeds-grid-limits",
        children: "Range {...} exceeds grid limits"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Range {...} exceeds ", _jsx(_components.span, {
            className: "hljs-attribute",
            children: "grid"
          }), " limits\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The ", _jsx(_components.a, {
          href: "#sheet-selection",
          children: "selected sheet"
        }), " or a data range within it is ", _jsx(_components.a, {
          href: "https://support.google.com/docs/answer/1218656",
          children: "protected"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Remove the protection from the sheet or range in Google Sheets. See ", _jsx(_components.a, {
          href: "https://support.google.com/docs/answer/1218656",
          children: "Google's documentation"
        }), " for details."]
      }), "\n", _jsx(_components.h4, {
        id: "requested-writing-within-rangebut-tried-writing-to-column",
        children: "Requested writing within range...but tried writing to column"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Requested writing within range ", _jsxs(_components.span, {
            className: "hljs-selector-attr",
            children: ["[", _jsx(_components.span, {
              className: "hljs-string",
              children: "'Example:Sheet'"
            }), "!A1]"]
          }), ", but tried writing ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " column ", _jsx(_components.span, {
            className: "hljs-selector-attr",
            children: "[B]"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sheet name contains a colon, which Google Sheets can't parse in range references."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Replace colons in the sheet name with an underscore or remove them entirely."]
      }), "\n", _jsx(_components.h4, {
        id: "the-caller-does-not-have-permission",
        children: "The caller does not have permission"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["The ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "caller"
          }), " does not have permission\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sheet is private and not shared with the authenticated account."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Share the sheet with the ", _jsx(_components.a, {
          href: "#connect-to-google-sheets",
          children: "user or service account you used to set up your Google Sheets destination"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "unable-to-parse-range",
        children: "Unable to parse range"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: ["Unable ", _jsx(_components.span, {
            className: "hljs-selector-tag",
            children: "to"
          }), " parse range\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sheet selected in the sync configuration was renamed or deleted."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Select an existing sheet in the ", _jsx(_components.a, {
          href: "#sheet-selection",
          children: "sheet selection"
        }), " dropdown."]
      }), "\n", _jsx(_components.h4, {
        id: "permission-errors-using-a-sheet-id",
        children: "Permission errors using a sheet ID"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-vbnet",
          children: ["Permission denied ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "when"
          }), " writing ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "to"
          }), " sheet ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "by"
          }), " ID\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Google Sheets requires the sheet to be selected through the picker before it can be referenced by ID."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Select the sheet in the sheets picker first, then use its ID."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
